import { Injectable } from '@angular/core';

const STORAGE_KEY = 'trustedDevices';
const MAX_ENTRIES = 10;
const LEGACY_TOKEN_KEY = 'trustedDeviceToken';
const LEGACY_EXPIRES_KEY = 'trustedDeviceExpiresAt';
const LEGACY_USER_KEY = 'trustedDeviceUser';

const normalize = (username: string): string => (username ?? '').trim().toLowerCase();

const isExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return false;

  const timestamp = Date.parse(expiresAt);
  return isNaN(timestamp) || timestamp <= Date.now();
};

interface TrustedDeviceEntry {
  user: string;
  token: string;
  expiresAt: string | null;
  lastUsedAt: number;
}

const toEntry = (raw: unknown): TrustedDeviceEntry | null => {
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Partial<TrustedDeviceEntry>;
  const user = normalize(typeof candidate.user === 'string' ? candidate.user : '');
  const token = typeof candidate.token === 'string' ? candidate.token : '';
  if (!user || !token) return null;

  return {
    user,
    token,
    expiresAt: typeof candidate.expiresAt === 'string' ? candidate.expiresAt : null,
    lastUsedAt: typeof candidate.lastUsedAt === 'number' && isFinite(candidate.lastUsedAt) ? candidate.lastUsedAt : 0,
  };
};

@Injectable({ providedIn: 'root' })
export class TrustedDeviceService {
  tokenFor(username: string): string | null {
    const user = normalize(username);
    if (!user) return null;

    const entries = this.load();
    const entry = entries.find((e) => e.user === user);
    if (entry) entry.lastUsedAt = Date.now();
    this.save(entries);
    return entry?.token ?? null;
  }

  store(username: string, token: string | null, expiresAt: string | null): void {
    const user = normalize(username);
    if (!user || !token) return;

    const entries = this.load().filter((e) => e.user !== user);
    entries.push({ user, token, expiresAt: expiresAt || null, lastUsedAt: Date.now() });
    this.save(entries);
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.clearLegacy();
  }

  private load(): TrustedDeviceEntry[] {
    const entries = this.readEntries();
    const legacy = this.readLegacy();
    if (legacy && !entries.some((e) => e.user === legacy.user)) entries.push(legacy);
    return entries.filter((e) => !isExpired(e.expiresAt));
  }

  private readEntries(): TrustedDeviceEntry[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map(toEntry).filter((e): e is TrustedDeviceEntry => e !== null);
    } catch {
      return [];
    }
  }

  private readLegacy(): TrustedDeviceEntry | null {
    try {
      const user = normalize(localStorage.getItem(LEGACY_USER_KEY) ?? '');
      const token = localStorage.getItem(LEGACY_TOKEN_KEY) ?? '';
      if (!user || !token) return null;

      return { user, token, expiresAt: localStorage.getItem(LEGACY_EXPIRES_KEY), lastUsedAt: Date.now() };
    } catch {
      return null;
    }
  }

  private save(entries: TrustedDeviceEntry[]): void {
    const top = [...entries].sort((a, b) => b.lastUsedAt - a.lastUsedAt).slice(0, MAX_ENTRIES);
    try {
      if (top.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(top));
      else localStorage.removeItem(STORAGE_KEY);
      this.clearLegacy();
    } catch (error) {
      console.error('TrustedDeviceService: could not persist trusted devices; login continues without them', error);
    }
  }

  private clearLegacy(): void {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_EXPIRES_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  }
}
